import { Box, Button, Divider, Grid, Paper, Typography } from "@mui/material";

import { useEffect, useRef, useState } from "react";

import { baseYears } from "pages/Dashboards/ProjectRegister/data/ProjectRegisterData";
import { LocationModel } from "object-models/location-model";
import { PortfolioFilter } from "../Data/PortfolioFilter";
import {
  _addPortfolioFilterSelection,
  _getBaselineYearByOrgId,
  _getPortfolioFilterSelection,
  _getTotalEmissionByScopes,
} from "middlewares/DashboardApi/portfolioView";
import { useAppState } from "context/appState.context";
import { _getLocations } from "middlewares/LocationApi/location";
import EmissionByScopePieChat from "./EmissionByScopePieChat";
import EmissionByScopeCategoryBar from "./EmissionByScopeCategoryBar";
import BaselineYearAndTotal from "./BaselineYearAndTotal";
import TotalEmissionsByScope from "./TotalEmissionsByScope";
import EmissionChartPanel from "./EmissionChartPanel";
import { AssessmentViewMode, EmissionData, scopesCoveredOptions } from "../Data/PortfolioViewData";
import Spinner from "components/Spinner";
import { Constants, PortfolioFilterPages } from "constants/constant";
import FilterPanel from "../FilterPanel";
import { PortfolioFilterSelection } from "../Data/PortfolioFilterSelection";

interface SelectedOrganization {
  orgId: number;
  orgName: string;
}

interface BaselineEmissionProps {
  isPortfolioView?: boolean;
  isClientPortfolio?: boolean;
  portfolioFilter?: PortfolioFilter;
  selectedScopeFilter?: number[];
  searchClickVersion?: number;
  selectedOrganizations?: SelectedOrganization[];
}

const BaselineEmission: React.FC<BaselineEmissionProps> = ({
  isPortfolioView = false,
  isClientPortfolio = false,
  portfolioFilter: externalPortfolioFilter,
  selectedScopeFilter,
  searchClickVersion,
  selectedOrganizations = [],
}) => {
  const { userRole } = useAppState();

  const [totalemissions, setTotalemissions] = useState<number>(0);
  const [data, setData] = useState([] as unknown as LocationModel);
  const [loading, setLoading] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState<number[]>([
    -1,
  ]);
  const [baseYear, setBaseYear] = useState<number>(0);
  const [emissionValues, setEmissionValues] = useState<{
    [key: string]: number;
  }>({});
  const [portfolioFilter, setPortfolioFilter] = useState<PortfolioFilter>({
    LocationId: [],
    BaseYear: [baseYear],
  });
  const shouldCallGetTotalEmission = useRef(false);
  const lastPortfolioRequestKey = useRef("");

  const [locationOptions, setLocationOptions] = useState<
    { id: number; label: string; value: string }[]
  >([]);

  const [selectedScope, setSelectedScope] = useState(
    scopesCoveredOptions.map((a) => a.id)
  );
  const [assessmentView, setAssessmentView] = useState<AssessmentViewMode>("organization");
  const [rawEmissionRows, setRawEmissionRows] = useState<Array<Partial<LocationModel>>>([]);
  const isOrganizationPortfolio =
    isPortfolioView && !isClientPortfolio && selectedOrganizations.length > 0;
  const selectedOrganizationId =
    selectedOrganizations.length === 1 ? selectedOrganizations[0].orgId : undefined;
  const storedOrganizationId = Number(localStorage.getItem("orgId"));
  const baselineOrganizationId =
    selectedOrganizationId ??
    (!isPortfolioView && storedOrganizationId > 0 ? storedOrganizationId : undefined);

  const organizationNameById = selectedOrganizations.reduce<Record<number, string>>(
    (accumulator, organization) => {
      if (organization?.orgId != null && organization?.orgName) {
        accumulator[organization.orgId] = organization.orgName;
      }
      return accumulator;
    },
    {}
  );

  useEffect(() => {
    if (!isPortfolioView || !selectedScopeFilter) {
      return;
    }

    if (selectedScopeFilter.length === 0) {
      setSelectedScope(scopesCoveredOptions.map((a) => a.id));
      return;
    }

    const mappedScopeIds = selectedScopeFilter
      .map((scopeId) => scopeId - 1)
      .filter((scopeId) => scopeId >= 0 && scopeId <= 2);

    setSelectedScope(mappedScopeIds);
  }, [isPortfolioView, selectedScopeFilter]);

  useEffect(() => {
    let isCurrentRequest = true;

    if (!baselineOrganizationId) {
      setBaseYear(0);
      return () => {
        isCurrentRequest = false;
      };
    }

    _getBaselineYearByOrgId(baselineOrganizationId)
      .then((year) => {
        if (!isCurrentRequest) return;

        const resolvedYear = Number(year) > 0 ? Number(year) : 0;
        setBaseYear(resolvedYear);
        setPortfolioFilter((prevFilter) => ({
          ...prevFilter,
          BaseYear: resolvedYear > 0 ? [resolvedYear] : [],
        }));
      })
      .catch((err) => {
        console.error("Error:", err);
        if (isCurrentRequest) setBaseYear(0);
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [baselineOrganizationId]);

  const handleScopeChange = (selectedOptions: number[]) => {
    setSelectedScope(selectedOptions);
    updateEmissionValues(selectedOptions);
  };

  const updateEmissionValues = (selectedOptions: number[]) => {
    const newEmissionValues: { [key: string]: number } = {
      "Scope 1": 0,
      "Scope 2": 0,
      "Scope 3": 0,
    };

    selectedOptions.forEach((option) => {
      switch (option) {
        case 0:
          newEmissionValues["Scope 1"] = Number(data.scope1GHGEmission) || 0;
          break;
        case 1:
          newEmissionValues["Scope 2"] = Number(data.scope2GHGEmission) || 0;
          break;
        case 2:
          newEmissionValues["Scope 3"] = Number(data.scope3GHGEmission) || 0;
          break;
      }
    });

    setEmissionValues(newEmissionValues);
    setTotalemissions(
      newEmissionValues["Scope 1"] +
        newEmissionValues["Scope 2"] +
        newEmissionValues["Scope 3"]
    );
  };

  const handleLocationChange = (selectedOptions: number[]) => {
    setSelectedLocations(selectedOptions);
    setPortfolioFilter((prevFilter) => ({
      ...prevFilter,
      LocationId: selectedOptions,
    }));
  };

  const handleBaseYearChange = (value: number) => {
    const selectedYear = value;
    setBaseYear(selectedYear);
    setPortfolioFilter((prevFilter) => ({
      ...prevFilter,
      BaseYear: [selectedYear],
    }));
  };

  function getEssentialLocationDetails() {
    _getLocations()
      .then((res) => {
        if (res) {
          const formattedOptions = res.map((loc) => ({
            id: loc.locationId,
            label: loc.locationName,
            value: loc.locationId.toString(),
          }));
          const selectAllOption = {
            id: 0,
            label: "Select All",
            value: "0",
          };
          setLocationOptions([selectAllOption, ...formattedOptions]);
        }
      })
      .catch((err) => {
        console.error("Error:", err);
      });
  }

  const getPortfolioFilterSelection = async (): Promise<void> => {
    try {
      const res = await _getPortfolioFilterSelection(
        PortfolioFilterPages.BaseLineEmissions
      );
      if (res) {
        const { locationIds, baseYear, scopes } = res;

        // Update state variables based on the response
        const parsedLocations = locationIds
          .split(",")
          .map((id: string) => parseInt(id, 10));
        const parsedBaseYear = baseYear ?? new Date().getFullYear();
        const parsedScopes = (scopes ?? "0,1,2")
          .split(",")
          .map((scope: string) => parseInt(scope, 10));
        setSelectedLocations(Array.isArray(parsedLocations) && parsedLocations.every(Number.isInteger) ? parsedLocations : []);
        setBaseYear(parsedBaseYear);
        setSelectedScope(Array.isArray(parsedScopes) && parsedScopes.every(Number.isInteger) ? parsedScopes : []);
        
        setPortfolioFilter((prevFilter) => {
          const updatedFilter = {
            ...prevFilter,
            LocationId: Array.isArray(parsedLocations) && parsedLocations.every(Number.isInteger) ? parsedLocations : [],
            BaseYear: [parsedBaseYear],
            userRole: userRole,
          };
          return updatedFilter;
        });
        shouldCallGetTotalEmission.current = true;
      }
    } catch (err) {
      console.error("Error:", err);
      setLoading(false);
    }
  };

  const getTotalEmissionByScopes = async (
    filterModel?: PortfolioFilter
  ): Promise<void> => {
    try {
      setLoading(true);
      const model = filterModel ?? portfolioFilter;
      if (!model) {
        return;
      }

      const res = await _getTotalEmissionByScopes(model);

      if (!res || !Array.isArray(res)) return;

      setRawEmissionRows(res as Array<Partial<LocationModel>>);

      const emissionSums: EmissionData = {};
      const totalEmissions: EmissionData = {
        "Scope 1": 0,
        "Scope 2": 0,
        "Scope 3": 0,
      };

      const aggregateEmissions = (
        key: string,
        value: number | null | undefined
      ) => {
        if (typeof value === "number") {
          emissionSums[key] = (emissionSums[key] ?? 0) + value;
        }
      };

      res.forEach((loc: Partial<LocationModel>) => {
        // Aggregate scope emissions
        totalEmissions["Scope 1"] += Number(loc.scope1GHGEmission ?? 0);
        totalEmissions["Scope 2"] += Number(loc.scope2GHGEmission ?? 0);
        totalEmissions["Scope 3"] += Number(loc.scope3GHGEmission ?? 0);

        // Aggregate other emissions
        Object.entries(loc).forEach(([key, value]) => {
          if (key !== "locationId") {
            aggregateEmissions(key, Number(value));
          }
        });
      });

      setData({
        locationId: 1,
        ...emissionSums,
      } as LocationModel);

      setEmissionValues(totalEmissions);
      setTotalemissions(
        totalEmissions["Scope 1"] +
          totalEmissions["Scope 2"] +
          totalEmissions["Scope 3"]
      );
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const savePortfolioFilterSelection = async () => {
    const portfolioFilterSelection: PortfolioFilterSelection = {
      userEmail: null,
      pageName: PortfolioFilterPages.BaseLineEmissions,
      locationIds: selectedLocations.join(","),
      baseYear: baseYear,
      scopes: selectedScope.join(","),
    };

    setPortfolioFilter((prevFilter) => ({
      ...prevFilter,
      BaseYear: [baseYear],
    }));
    try {
      const res = await _addPortfolioFilterSelection(portfolioFilterSelection);
      if (res) {
        getTotalEmissionByScopes();
      }
    } catch (err) {
      console.error("Error:", err);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isPortfolioView) {
      return;
    }
    if (shouldCallGetTotalEmission.current) {
      getTotalEmissionByScopes();
      shouldCallGetTotalEmission.current = false;
    }
  }, [isPortfolioView, portfolioFilter]);

  useEffect(() => {
    if (isPortfolioView) {
      return;
    }
    getEssentialLocationDetails();
    getPortfolioFilterSelection();
  }, [isPortfolioView]);

  useEffect(() => {
    if (
      !isPortfolioView ||
      !externalPortfolioFilter ||
      (searchClickVersion ?? 0) < 1
    ) {
      return;
    }

    const sortedLocationIds = [...externalPortfolioFilter.LocationId].sort(
      (left, right) => left - right
    );
    const sortedBaseYears = [...externalPortfolioFilter.BaseYear].sort(
      (left, right) => left - right
    );
    const requestKey = `${sortedBaseYears.join(",")}|${sortedLocationIds.join(",")}`;
    lastPortfolioRequestKey.current = requestKey;

    getTotalEmissionByScopes(externalPortfolioFilter);
  }, [externalPortfolioFilter, isPortfolioView, searchClickVersion]);

  return (
    <Box>
      {!isPortfolioView && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} sx={{ mr: 5 }}>
              <Typography
                variant="h4"
                sx={{ fontWeight: "bold" }}
                data-testid="portfolioviewId"
              >
                Portfolio view
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontWeight: "bold" }}
                data-testid="baselineEmissionsId"
              >
                Baseline Emissions
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ my: 1 }} />
        </>
      )}

      {!isPortfolioView && (
        <Grid container spacing={2} sx={{ width: '100%', maxWidth: '100%' }}>
          <Grid item xs={12} sx={{ width: '100%', maxWidth: '100%' }}>
            <FilterPanel
              locationOptions={locationOptions}
              selectedLocations={selectedLocations}
              onLocationChange={handleLocationChange}
              showBaseYear={true}
              baseYear={baseYear}
              baseYearOptions={baseYears}
              onBaseYearChange={handleBaseYearChange}
              showScope={true}
              scopeOptions={scopesCoveredOptions}
              selectedScope={selectedScope}
              onScopeChange={handleScopeChange}
              onSearch={() => {
                savePortfolioFilterSelection();
              }}
            />
          </Grid>
        </Grid>
      )}

      <Divider sx={{ my: 1 }} />
      {loading ? (
        <Spinner size={80} data-testid="spinner" />
      ) : (
        <>
          <Grid container spacing={2} sx={{ width: '100%', maxWidth: '100%' }}>
            <Grid item xs={4.8} sx={{ width: '100%', maxWidth: '100%' }}>
              <BaselineYearAndTotal
                totalEmissions={totalemissions}
                baseYear={baseYear}
              />
            </Grid>
            <Grid item xs={7.2} sx={{ width: '100%', maxWidth: '100%' }}>
              <TotalEmissionsByScope
                emissionValues={emissionValues}
                selectedScope={selectedScope}
              />
            </Grid>
          </Grid>
          <EmissionChartPanel
            headerAction={
              <Box
                sx={{
                  display: "inline-flex",
                  backgroundColor: "#fff",
                  border: "1px solid #d5dce5",
                  borderRadius: "12px",
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                <Button
                  onClick={() => setAssessmentView("scope")}
                  sx={{
                    textTransform: "none",
                    borderRadius: 0,
                    px: 1.5,
                    py: 0.55,
                    minWidth: 106,
                    fontSize: "0.80rem",
                    fontWeight: 590,
                    color: assessmentView === "scope" ? "#fff" : "#2c3e50",
                    backgroundColor:
                      assessmentView === "scope" ? "#e3812a" : "transparent",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      backgroundColor:
                        assessmentView === "scope" ? "#d67624" : "#f5f7fa",
                    },
                  }}
                >
                  View by Scope
                </Button>
                <Button
                  onClick={() => setAssessmentView("organization")}
                  sx={{
                    textTransform: "none",
                    borderRadius: 0,
                    px: 1.5,
                    py: 0.55,
                    minWidth: 132,
                    fontSize: "0.80rem",
                    fontWeight: 590,
                    color: assessmentView === "organization" ? "#fff" : "#2c3e50",
                    backgroundColor:
                      assessmentView === "organization" ? "#e3812a" : "transparent",
                    borderLeft: "1px solid #d5dce5",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      backgroundColor:
                        assessmentView === "organization" ? "#d67624" : "#f5f7fa",
                    },
                  }}
                >
                  {isOrganizationPortfolio ? "View by Location" : "View by Organization"}
                </Button>
              </Box>
            }
          >
            <Grid container sx={{ width: "100%", maxWidth: "100%" }}>
              <EmissionByScopePieChat
                data={data}
                selectedScope={selectedScope}
                assessmentView={assessmentView}
                rawRows={rawEmissionRows}
                organizationNameById={organizationNameById}
                isOrganizationPortfolio={isOrganizationPortfolio}
              />
              <EmissionByScopeCategoryBar
                data={data}
                assessmentView={assessmentView}
                rawRows={rawEmissionRows}
                organizationNameById={organizationNameById}
                selectedScope={selectedScope}
                isOrganizationPortfolio={isOrganizationPortfolio}
              />
            </Grid>
          </EmissionChartPanel>
        </>
      )}
    </Box>
  );
};
export default BaselineEmission;
